import { useAuth } from '@clerk/clerk-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Custom hook that returns an authenticated axios instance using Clerk JWT
export function useAuthAxios() {
    const { getToken } = useAuth();
    const navigate = useNavigate();

    const authAxios = useMemo(() => {
        const instance = axios.create({
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Request interceptor - adds auth token to every request
        instance.interceptors.request.use(
            async (config) => {
                const token = await getToken({ template: "detailed_token" });
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor - handles 401 errors
        instance.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    console.log("status 401 - unauthorized");
                    navigate("/");
                }
                return Promise.reject(error);
            }
        );

        return instance;
    }, [getToken, navigate]);

    return authAxios;
}

// Legacy wrapper for backward compatibility with old authfetch signature
// Usage: const authfetch = useAuthFetch(); authfetch(url, options)
export function useAuthFetch() {
    const authAxios = useAuthAxios();

    return async (url, options = {}) => {
        const { method = 'GET', body, headers = {} } = options;
        
        const config = {
            url,
            method,
            headers,
            data: body ? (typeof body === 'string' ? JSON.parse(body) : body) : undefined,
        };

        const response = await authAxios.request(config);
        
        // Return fetch-like response for backward compatibility
        return {
            ok: response.status >= 200 && response.status < 300,
            status: response.status,
            json: async () => response.data,
            data: response.data,
        };
    };
}

export default useAuthFetch;